import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Plus, Pencil, Trash2, X, Zap, MousePointerClick, Target } from "lucide-react";
import api from "../api/client";
import BehaviorNav from "./BehaviorNav";

const RANGES = ["7d", "30d", "90d"];

function StatCard({ icon: Icon, label, value, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-dark-50/50 rounded-lg">
          <Icon size={18} className={color} />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className="text-2xl font-mono font-bold">{value ?? "—"}</div>
    </div>
  );
}

function RuleModal({ rule, onClose, onSave, websiteId }) {
  const [form, setForm] = useState(
    rule || {
      name: "",
      signal_type: "exit_intent",
      priority: "medium",
      enabled: true,
      conditions: {},
      action_config: {},
    }
  );
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    const payload = { ...form, website: websiteId };
    const req = form.id
      ? api.patch(`/signals/rules/${form.id}/`, payload)
      : api.post("/signals/rules/", payload);
    req
      .then((res) => onSave(res.data))
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-dark-300 border border-dark-50 rounded-xl w-full max-w-lg mx-4 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-semibold text-gray-200">
            {form.id ? "Edit Rule" : "Create Rule"}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-dark-50/50 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-200"
              placeholder="Rule name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select
                value={form.signal_type}
                onChange={(e) =>
                  setForm({ ...form, signal_type: e.target.value })
                }
                className="w-full bg-dark-50/50 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-200"
              >
                <option value="exit_intent">Exit Intent</option>
                <option value="high_engagement">High Engagement</option>
                <option value="scroll_depth">Scroll Depth</option>
                <option value="rage_click">Rage Click</option>
                <option value="idle_user">Idle User</option>
                <option value="returning_visitor">Returning Visitor</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Priority
              </label>
              <select
                value={form.priority}
                onChange={(e) =>
                  setForm({ ...form, priority: e.target.value })
                }
                className="w-full bg-dark-50/50 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-200"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Conditions (JSON)
            </label>
            <textarea
              value={JSON.stringify(form.conditions, null, 2)}
              onChange={(e) => {
                try {
                  setForm({ ...form, conditions: JSON.parse(e.target.value) });
                } catch {}
              }}
              rows={3}
              className="w-full bg-dark-50/50 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-200 font-mono"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Action Config (JSON)
            </label>
            <textarea
              value={JSON.stringify(form.action_config, null, 2)}
              onChange={(e) => {
                try {
                  setForm({
                    ...form,
                    action_config: JSON.parse(e.target.value),
                  });
                } catch {}
              }}
              rows={3}
              className="w-full bg-dark-50/50 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-200 font-mono"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) =>
                setForm({ ...form, enabled: e.target.checked })
              }
              className="rounded border-dark-50"
            />
            <span className="text-sm text-gray-300">Enabled</span>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name}
            className="px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BehaviorTriggers({ selectedSite }) {
  const [rules, setRules] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [editRule, setEditRule] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    Promise.all([
      api.get("/signals/rules/", {
        params: { website_id: selectedSite.id },
      }),
      api.get("/signals/analytics/", {
        params: { website_id: selectedSite.id, range },
      }),
    ])
      .then(([rulesRes, analyticsRes]) => {
        setRules(rulesRes.data.results || rulesRes.data);
        setAnalytics(analyticsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSite, range]);

  const toggleEnabled = (rule) => {
    api
      .patch(`/signals/rules/${rule.id}/`, { enabled: !rule.enabled })
      .then((res) => {
        setRules(rules.map((r) => (r.id === rule.id ? res.data : r)));
      })
      .catch(() => {});
  };

  const deleteRule = (rule) => {
    api
      .delete(`/signals/rules/${rule.id}/`)
      .then(() => {
        setRules(rules.filter((r) => r.id !== rule.id));
        setDeleteConfirm(null);
      })
      .catch(() => {});
  };

  const handleSave = (saved) => {
    if (editRule) {
      setRules(rules.map((r) => (r.id === saved.id ? saved : r)));
      setEditRule(null);
    } else {
      setRules([...rules, saved]);
      setShowCreate(false);
    }
  };

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to manage triggers
      </div>
    );
  }

  const stats = analytics?.stats || {};
  const daily = (analytics?.daily || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    fired: d.fired,
    converted: d.converted,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Signal Triggers</h2>
        <div className="flex items-center gap-3">
          <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  range === r
                    ? "bg-copper/20 text-copper"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={14} />
            New Rule
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">

      <BehaviorNav />
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-28 bg-dark-300 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-dark-300 rounded-xl" />
        </div>
      ) : (
        <>
          {/* Analytics cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              icon={Zap}
              label="Total Fired"
              value={stats.total_fired?.toLocaleString()}
            />
            <StatCard
              icon={MousePointerClick}
              label="Click Rate"
              value={
                stats.click_rate != null
                  ? `${stats.click_rate.toFixed(1)}%`
                  : undefined
              }
              color="text-blue-400"
            />
            <StatCard
              icon={Target}
              label="Conversion Rate"
              value={
                stats.conversion_rate != null
                  ? `${stats.conversion_rate.toFixed(1)}%`
                  : undefined
              }
              color="text-green-400"
            />
          </div>

          {/* Daily signals chart */}
          {daily.length > 1 && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-300 mb-4">
                Daily Signals
              </h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <YAxis
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#111827",
                        border: "1px solid #1e293b",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="fired"
                      stroke="#b87333"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="converted"
                      stroke="#22c55e"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Rules table */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-dark-50">
              <h3 className="text-sm font-semibold text-gray-300">
                Signal Rules
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-dark-50">
                    <th className="text-left py-2.5 px-4 font-medium">Name</th>
                    <th className="text-left py-2.5 px-3 font-medium">Type</th>
                    <th className="text-left py-2.5 px-3 font-medium">Priority</th>
                    <th className="text-center py-2.5 px-3 font-medium">Enabled</th>
                    <th className="text-right py-2.5 px-3 font-medium">Fired</th>
                    <th className="text-right py-2.5 px-3 font-medium">Converted</th>
                    <th className="text-right py-2.5 px-3 font-medium">Conv Rate</th>
                    <th className="text-center py-2.5 px-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr
                      key={rule.id}
                      className="border-b border-dark-50/50 hover:bg-dark-50/30"
                    >
                      <td className="py-2.5 px-4 text-gray-300">{rule.name}</td>
                      <td className="py-2.5 px-3 text-xs text-gray-400 capitalize">
                        {rule.signal_type?.replace("_", " ")}
                      </td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`text-xs px-2 py-0.5 rounded font-medium ${
                            rule.priority === "critical"
                              ? "text-red-400 bg-red-900/20"
                              : rule.priority === "high"
                              ? "text-amber-400 bg-amber-900/20"
                              : rule.priority === "medium"
                              ? "text-blue-400 bg-blue-900/20"
                              : "text-gray-400 bg-gray-800"
                          }`}
                        >
                          {rule.priority}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          onClick={() => toggleEnabled(rule)}
                          className={`w-8 h-4 rounded-full transition-colors relative ${
                            rule.enabled ? "bg-copper" : "bg-dark-50"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                              rule.enabled ? "left-4" : "left-0.5"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                        {rule.fired_count?.toLocaleString() || 0}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                        {rule.converted_count?.toLocaleString() || 0}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                        {rule.fired_count
                          ? `${((rule.converted_count / rule.fired_count) * 100).toFixed(1)}%`
                          : "—"}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setEditRule(rule)}
                            className="p-1 hover:bg-dark-50/50 rounded transition-colors"
                          >
                            <Pencil size={14} className="text-gray-400" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(rule)}
                            className="p-1 hover:bg-dark-50/50 rounded transition-colors"
                          >
                            <Trash2 size={14} className="text-gray-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!rules.length && (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-12 text-center text-gray-500"
                      >
                        No signal rules configured
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create/Edit modal */}
      {(showCreate || editRule) && (
        <RuleModal
          rule={editRule}
          websiteId={selectedSite.id}
          onClose={() => {
            setShowCreate(false);
            setEditRule(null);
          }}
          onSave={handleSave}
        />
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-dark-300 border border-dark-50 rounded-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-200 mb-3">
              Delete Rule
            </h3>
            <p className="text-sm text-gray-400 mb-6">
              Are you sure you want to delete "{deleteConfirm.name}"? This
              action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteRule(deleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
